import EventEmitter from "eventemitter3";
import ReconnectingWebSocket from "reconnecting-websocket";
import { ITranscriptionService } from "../interfaces/ITranscriptionService";
import {
  TelemetryRow,
  TelemetrySnapshot,
  TranscriptionMetricsProvider,
  WhisperTelemetryCollector,
} from "../metrics";
import {
  DurableAudioSessionManager,
  ReplayOrchestrator,
} from "../recovery";
import {
  BATCH_REPROCESS_TTL_MS,
  BatchReprocessConfig,
  BatchReprocessError,
  BatchReprocessStatus,
  DEFAULT_BATCH_REPROCESS_PATH,
  ResolvedWhisperConnectionContext,
  createBatchReprocessStatus,
  getBatchReprocessRemainingTime,
  refreshBatchReprocessStatus,
} from "../batchReprocess";
import type { DebugAuditFile } from "../audit";
import {
  AUTH_REJECTED_CLOSE_CODE,
  DirectModeAuth,
  SofyaAuthError,
  resolveBearerToken,
} from "../auth";
import {
  DEFAULT_STT_AUDIT_INGESTION_PATH,
  STT_AUDIT_INGESTION_RETRY_DELAYS_MS,
  SttAuditIngestionConfig,
  SttAuditIngestionPayload,
  SttAuditIngestionWarning,
} from "../sttAuditIngestion";

interface IConnectionResilienceConfig {
  connectTimeoutMs?: number;
  healthCheckIntervalMs?: number;
  maxBufferedAmountBytes?: number;
  drainStallTimeoutMs?: number;
  preRecoveryReplayMs?: number;
  reconnectBaseDelayMs?: number;
  reconnectMaxDelayMs?: number;
  reconnectDelayGrowFactor?: number;
  minConnectionUptimeMs?: number;
  maxReconnectAttempts?: number;
}

interface ITranscriptionConfig {
  language: string;
  translation_lang?: string;
  external_id?: string;
  record?: boolean;
  token?: string;
  headers?: Record<string, string>;
  resolvedEndpointUrl?: string;
  resolvedConnectionContext?: ResolvedWhisperConnectionContext;
  ws: ReconnectingWebSocket;
  provider?: TranscriptionMetricsProvider;
  resilience?: IConnectionResilienceConfig;
  finalUpload?: {
    mode?: "stream" | "blob";
    format?: "pcm" | "wav";
    endpoint?: string;
  };
  batchReprocess?: BatchReprocessConfig<unknown>;
  auditIngestion?: SttAuditIngestionConfig;
  debug?:
    | boolean
    | {
        enabled?: boolean;
        advancedMetrics?: boolean;
      };
}

interface IDisconnectDetails {
  code?: number;
  reason?: string;
  wasClean?: boolean;
  error?: Error;
}

type ResilienceConnectionState =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "stopped";

type ResilienceWebSocketState =
  | "connecting"
  | "open"
  | "closing"
  | "closed"
  | "unavailable";

type DrainExitReason =
  | "completed"
  | "socket_unavailable"
  | "send_failed"
  | "stalled"
  | "stopped";

type FinishDeliveryState =
  | "sent"
  | "skipped_socket_closed"
  | "timed_out";

type LifecyclePhase =
  | "idle"
  | "starting_preflight"
  | "starting_bootstrap"
  | "running_live"
  | "running_recovering"
  | "paused"
  | "stopping_freeze_input"
  | "stopping_finalize_server"
  | "stopping_cleanup"
  | "stopped"
  | "failed";

interface StopFinalizationState {
  resolve: () => void;
  timeoutId: number;
  finishSent: boolean;
  settled: boolean;
}

/**
 * Campo de sessão que o servidor anexa a toda mensagem `partial`/`final`
 * (contrato SDK de gravação, item 3). Um `session_id` por conexão.
 */
interface SttSessionFields {
  session_id?: unknown;
}

export interface WhisperSttSessionInfo {
  sttSessionId: string;
  externalId: string | null;
  connectionAttempt: number;
}

const DEFAULT_RESILIENCE_CONFIG: Required<IConnectionResilienceConfig> = {
  connectTimeoutMs: 10000,
  healthCheckIntervalMs: 2000,
  maxBufferedAmountBytes: 262144,
  drainStallTimeoutMs: 5000,
  preRecoveryReplayMs: 500,
  reconnectBaseDelayMs: 1000,
  reconnectMaxDelayMs: 10000,
  reconnectDelayGrowFactor: 1.3,
  minConnectionUptimeMs: 5000,
  maxReconnectAttempts: 96,
};

const TRANSPORT_DRAIN_POLL_MS = 100;
const STOP_DRAIN_TIMEOUT_MS = 5000;
const AUDIO_CONTEXT_IDLE_CLOSE_MS = 15000;
const CONSULTATION_STATUS_REFRESH_MIN_INTERVAL_MS = 250;
const REALTIME_AUDIO_PROCESSOR_NAME = "realtime-audio-processor";
const LIFECYCLE_TRANSITIONS: Record<LifecyclePhase, LifecyclePhase[]> = {
  idle: [
    "starting_preflight",
    "running_live",
    "running_recovering",
    "paused",
    "stopping_freeze_input",
    "failed",
  ],
  starting_preflight: ["starting_bootstrap", "failed"],
  starting_bootstrap: ["running_live", "failed"],
  running_live: [
    "paused",
    "running_recovering",
    "stopping_freeze_input",
    "starting_preflight",
    "failed",
  ],
  running_recovering: [
    "running_live",
    "paused",
    "stopping_freeze_input",
    "starting_preflight",
    "failed",
  ],
  paused: [
    "running_live",
    "running_recovering",
    "stopping_freeze_input",
    "starting_preflight",
    "failed",
  ],
  stopping_freeze_input: ["stopping_finalize_server", "stopping_cleanup", "failed"],
  stopping_finalize_server: ["stopping_cleanup", "failed"],
  stopping_cleanup: ["stopped", "failed"],
  stopped: ["starting_preflight", "failed"],
  failed: ["starting_preflight", "stopping_freeze_input", "stopped"],
};
const isAdvancedMetricsEnabled = (
  debugConfig: ITranscriptionConfig["debug"]
): boolean => {
  if (!debugConfig) {
    return false;
  }

  if (debugConfig === true) {
    return true;
  }

  if (debugConfig.enabled === false) {
    return false;
  }

  return debugConfig.advancedMetrics ?? true;
};

const REALTIME_AUDIO_WORKLET_CODE = `
class RealtimeAudioProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.active = true;
    this.port.onmessage = (event) => {
      if (event?.data?.type === "sofya-stop") {
        this.active = false;
      }
    };
  }

  process(inputs, outputs, params) {
    if (!this.active) {
      return false;
    }

    const channel = inputs[0]?.[0];
    if (channel) {
      // ASR and VAD models typically require mono audio.
      this.port.postMessage(channel);
    }

    return true;
  }
}

registerProcessor("${REALTIME_AUDIO_PROCESSOR_NAME}", RealtimeAudioProcessor);
`;

export class WhisperTranscriptionAdapter
  extends EventEmitter
  implements ITranscriptionService
{
  private static sharedAudioContext: AudioContext | null = null;
  private static sharedAudioContextLeaseCount = 0;
  private static sharedAudioContextIdleCloseTimer: number | null = null;
  private static sharedWorkletModuleReadyPromise: Promise<void> | null = null;
  private static sharedWorkletBlobUrl: string | null = null;
  private websocket: ReconnectingWebSocket | null = null;
  private readonly websocketFactoryConfig: {
    url: string;
    protocols?: string | string[];
    options?: unknown;
  };
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private input: MediaStreamAudioSourceNode | undefined;
  private tempPartialTranscription: string | undefined;
  // Sessões do servidor (uma por conexão) acumuladas desde o último start.
  private connectionSequence = 0;
  private currentSttSessionId: string | null = null;
  private sttSessionIds: string[] = [];
  private recordState: string = "PAUSED";
  private recordingNode: AudioWorkletNode | undefined = undefined;
  private readonly resilienceConfig: Required<IConnectionResilienceConfig>;
  private readonly finalUploadConfig?: ITranscriptionConfig["finalUpload"];
  private readonly batchReprocessConfig?: ITranscriptionConfig["batchReprocess"];
  private readonly sttAuditIngestionConfig?: ITranscriptionConfig["auditIngestion"];
  private readonly resolvedConnectionContext?: ResolvedWhisperConnectionContext;
  private readonly metricsCollector: WhisperTelemetryCollector;
  private durableAudioSession: DurableAudioSessionManager | null = null;
  private replayOrchestrator: ReplayOrchestrator | null = null;
  private durableReplayInFlight = false;
  private lastProducedSeq = 0;
  private latestConsultationStorageStatus: Awaited<
    ReturnType<DurableAudioSessionManager["getConsultationStorageStatus"]>
  > | null = null;
  private consultationStatusRefreshTimer: number | null = null;
  private consultationStatusRefreshInFlight = false;
  private consultationStatusRefreshQueued = false;
  private lastConsultationStatusRefreshAt = 0;
  private healthCheckTimer: number | null = null;
  private batchReprocessExpiryTimer: number | null = null;
  private intentionalStop = false;
  private hasConnectedOnce = false;
  private awaitingReconnection = false;
  private lastReconnectAttempt = 0;
  private suppressNextReconnectEvent = false;
  private pendingDisconnectDetails: IDisconnectDetails | null = null;
  private finishDeliveryState: FinishDeliveryState | null = null;
  private stopFinalizationState: StopFinalizationState | null = null;
  private stopInFlightPromise: Promise<Blob | null> | null = null;
  private connectionState: ResilienceConnectionState = "connecting";
  private currentReconnectAttempt: number | null = null;
  private nextReconnectDelayMs: number | null = null;
  private lastDisconnectDetails: IDisconnectDetails | null = null;
  private hasAudioContextLease = false;
  private browserOnline =
    typeof navigator === "undefined" ? true : navigator.onLine !== false;
  private terminalCleanupPromise: Promise<void> | null = null;
  private lifecyclePhase: LifecyclePhase = "idle";
  private batchReprocessStatus: BatchReprocessStatus;
  private readonly handleBrowserOnline = () => {
    this.browserOnline = true;
    this.emitResilienceStatus();

    if (
      this.awaitingReconnection &&
      this.websocket &&
      this.websocket.readyState === ReconnectingWebSocket.CLOSED &&
      !this.intentionalStop
    ) {
      const currentWebSocket = this.websocket as any;

      const replacement = new ReconnectingWebSocket(
        currentWebSocket._url as string,
        currentWebSocket._protocols,
        currentWebSocket._options as any
      );
      this.bindWebsocket(replacement);
    }
  };
  private readonly handleBrowserOffline = () => {
    this.browserOnline = false;
    this.emitResilienceStatus();
  };

  constructor(config: ITranscriptionConfig) {
    super();
    console.count("whisper:adapter_created");
    const advancedMetrics = isAdvancedMetricsEnabled(config.debug);
    this.finalUploadConfig = config.finalUpload;
    this.batchReprocessConfig = config.batchReprocess;
    this.sttAuditIngestionConfig = config.auditIngestion;
    this.resolvedConnectionContext =
      config.resolvedConnectionContext ??
      this.createLegacyResolvedConnectionContext(config);
    this.resilienceConfig = {
      ...DEFAULT_RESILIENCE_CONFIG,
      ...config.resilience,
    };
    this.metricsCollector = new WhisperTelemetryCollector({
      provider: config.provider ?? "sofya_as_service",
      onEmit: (snapshot: TelemetrySnapshot) => {
        this.emit("telemetry", snapshot);
      },
      onRow: (row: TelemetryRow) => {
        this.emit("telemetry_row", row);
      },
      advancedMetrics,
      inputSampleRate: 44100,
      targetSampleRate: 16000,
      channelCount: 1,
    });
    this.websocketFactoryConfig = this.extractWebSocketFactoryConfig(config.ws);
    this.setupDurableRuntime();
    this.websocket = config.ws;
    this.bindWebsocket(config.ws);
    this.requestPersistentStorage();
    this.batchReprocessStatus = this.createWaitingBatchReprocessStatus();
    this.emitResilienceStatus();
  }

  private extractWebSocketFactoryConfig(ws: ReconnectingWebSocket) {
    const transport = ws as unknown as {
      _url?: string;
      _protocols?: string | string[];
      _options?: unknown;
    };

    return {
      url: transport._url ?? "",
      protocols: transport._protocols,
      options: transport._options,
    };
  }

  private createLegacyResolvedConnectionContext(
    config: ITranscriptionConfig
  ): ResolvedWhisperConnectionContext | undefined {
    if (!config.resolvedEndpointUrl) {
      return undefined;
    }

    const runtimeBaseUrl =
      typeof window !== "undefined" && window.location?.href
        ? window.location.href
        : "http://localhost/";
    const realtimeUrl = new URL(config.resolvedEndpointUrl, runtimeBaseUrl);
    const batchBaseUrl = new URL("/", realtimeUrl.toString());
    batchBaseUrl.protocol =
      realtimeUrl.protocol === "wss:"
        ? "https:"
        : realtimeUrl.protocol === "ws:"
          ? "http:"
          : realtimeUrl.protocol;

    return {
      realtimeUrl: realtimeUrl.toString(),
      batchBaseUrl: batchBaseUrl.toString(),
      batchDefaultUrl: new URL(
        DEFAULT_BATCH_REPROCESS_PATH,
        batchBaseUrl.toString()
      ).toString(),
      authToken: resolveBearerToken(
        (config as { auth?: DirectModeAuth }).auth,
        config.token
      ),
      externalId: realtimeUrl.searchParams.get("x-external-id") ?? config.external_id,
      transcriptionLanguage:
        realtimeUrl.searchParams.get("transcription_language") ?? undefined,
      translationLanguage:
        realtimeUrl.searchParams.get("translation_language") ?? undefined,
      headers: config.headers ? { ...config.headers } : undefined,
    };
  }

  private createWaitingBatchReprocessStatus(): BatchReprocessStatus {
    const configured = this.isBatchReprocessConfigured();

    return createBatchReprocessStatus({
      state: configured ? "waiting_for_stop" : "unconfigured",
      supported: true,
      configured,
      available: false,
      lastStartedAt: null,
      lastCompletedAt: null,
      lastError: null,
    });
  }

  private isBatchReprocessConfigured(): boolean {
    return Boolean(
      this.batchReprocessConfig?.endpoint ||
        this.resolvedConnectionContext?.batchDefaultUrl
    );
  }

  private areBatchReprocessErrorsEqual(
    left: BatchReprocessError | null,
    right: BatchReprocessError | null
  ) {
    if (left === right) {
      return true;
    }

    if (!left || !right) {
      return false;
    }

    return (
      left.code === right.code &&
      left.message === right.message &&
      left.status === right.status
    );
  }

  private areBatchReprocessStatusesEqual(
    left: BatchReprocessStatus,
    right: BatchReprocessStatus
  ) {
    return (
      left.state === right.state &&
      left.supported === right.supported &&
      left.configured === right.configured &&
      left.available === right.available &&
      left.expiresAt === right.expiresAt &&
      left.remainingMs === right.remainingMs &&
      left.lastStartedAt === right.lastStartedAt &&
      left.lastCompletedAt === right.lastCompletedAt &&
      this.areBatchReprocessErrorsEqual(left.lastError, right.lastError)
    );
  }

  private clearBatchReprocessExpiryTimer() {
    if (this.batchReprocessExpiryTimer === null) {
      return;
    }

    window.clearTimeout(this.batchReprocessExpiryTimer);
    this.batchReprocessExpiryTimer = null;
  }

  private scheduleBatchReprocessExpiry(status: BatchReprocessStatus) {
    this.clearBatchReprocessExpiryTimer();

    if (
      status.expiresAt === null ||
      status.state === "expired" ||
      status.remainingMs === null ||
      status.remainingMs <= 0
    ) {
      return;
    }

    this.batchReprocessExpiryTimer = window.setTimeout(() => {
      this.batchReprocessExpiryTimer = null;
      this.setBatchReprocessStatus(
        createBatchReprocessStatus({
          ...this.batchReprocessStatus,
          state: "expired",
          available: false,
          expiresAt: this.batchReprocessStatus.expiresAt ?? Date.now(),
          now: Date.now(),
        })
      );
    }, status.remainingMs);
  }

  private setBatchReprocessStatus(nextStatus: BatchReprocessStatus) {
    const normalized = refreshBatchReprocessStatus(nextStatus);
    const previous = this.batchReprocessStatus;
    this.batchReprocessStatus = normalized;
    this.scheduleBatchReprocessExpiry(normalized);

    if (!previous || !this.areBatchReprocessStatusesEqual(previous, normalized)) {
      this.emit("batch_reprocess_status", { ...normalized });
    }
  }

  private syncBatchReprocessStatus(now = Date.now()) {
    const normalized = refreshBatchReprocessStatus(this.batchReprocessStatus, now);
    if (!this.areBatchReprocessStatusesEqual(this.batchReprocessStatus, normalized)) {
      this.setBatchReprocessStatus(normalized);
    }

    return this.batchReprocessStatus;
  }

  private transitionBatchReprocessStatusAfterStop(stopCompletedAt = Date.now()) {
    const current = this.syncBatchReprocessStatus(stopCompletedAt);
    const configured = this.isBatchReprocessConfigured();
    const expiresAt = stopCompletedAt + BATCH_REPROCESS_TTL_MS;
    const hasDurableAudio =
      (this.latestConsultationStorageStatus?.durableSegments ?? 0) > 0;

    if (!configured) {
      this.setBatchReprocessStatus(
        createBatchReprocessStatus({
          state: "unconfigured",
          supported: true,
          configured: false,
          available: false,
          expiresAt,
          lastStartedAt: current.lastStartedAt,
          lastCompletedAt: current.lastCompletedAt,
          lastError: current.lastError,
          now: stopCompletedAt,
        })
      );
      return;
    }

    if (!hasDurableAudio) {
      this.setBatchReprocessStatus(
        createBatchReprocessStatus({
          state: "expired",
          supported: true,
          configured: true,
          available: false,
          expiresAt: stopCompletedAt,
          lastStartedAt: current.lastStartedAt,
          lastCompletedAt: current.lastCompletedAt,
          lastError:
            current.lastError ?? new BatchReprocessError("AUDIO_EXPIRED"),
          now: stopCompletedAt,
        })
      );
      return;
    }

    this.setBatchReprocessStatus(
      createBatchReprocessStatus({
        state: "ready",
        supported: true,
        configured: true,
        available: true,
        expiresAt,
        lastStartedAt: current.lastStartedAt,
        lastCompletedAt: current.lastCompletedAt,
        lastError: current.lastError,
        now: stopCompletedAt,
      })
    );
  }

  private markBatchReprocessError(
    error: BatchReprocessError,
    state: BatchReprocessStatus["state"] = "error",
    now = Date.now()
  ) {
    const current = this.syncBatchReprocessStatus(now);
    this.setBatchReprocessStatus(
      createBatchReprocessStatus({
        state,
        supported: true,
        configured: current.configured,
        available: state === "error" && current.expiresAt !== null && current.expiresAt > now,
        expiresAt: current.expiresAt,
        lastStartedAt: current.lastStartedAt,
        lastCompletedAt: current.lastCompletedAt,
        lastError: error,
        now,
      })
    );
  }

  public getBatchReprocessStatus(): BatchReprocessStatus {
    return { ...this.syncBatchReprocessStatus() };
  }

  public getBatchReprocessRemainingTime(): number | null {
    return getBatchReprocessRemainingTime(this.syncBatchReprocessStatus());
  }

  public getSttSessionId(): string | null {
    return this.currentSttSessionId;
  }

  public getSttSessionIds(): string[] {
    return [...this.sttSessionIds];
  }

  private ensureWebSocketForStart() {
    const activeWebSocket = this.websocket;

    if (activeWebSocket) {
      this.bindWebsocket(activeWebSocket);
      if (activeWebSocket.readyState === ReconnectingWebSocket.CLOSED) {
        activeWebSocket.reconnect();
      }
      return;
    }

    if (!this.websocketFactoryConfig.url) {
      return;
    }

    const nextWebSocket = new ReconnectingWebSocket(
      this.websocketFactoryConfig.url,
      this.websocketFactoryConfig.protocols,
      this.websocketFactoryConfig.options as any
    );
    this.bindWebsocket(nextWebSocket);
  }

  private setupDurableRuntime() {
    this.durableAudioSession = new DurableAudioSessionManager({
    });

    this.replayOrchestrator = new ReplayOrchestrator(this.durableAudioSession, {
      isSocketOpen: () =>
        !!this.websocket &&
        this.websocket.readyState === ReconnectingWebSocket.OPEN,
      getSocket: () =>
        this.websocket &&
        this.websocket.readyState === ReconnectingWebSocket.OPEN
          ? (this.websocket as unknown as { readyState: number; send(data: ArrayBuffer): void })
          : null,
      waitForTransportDrain: async () => {
        if (!this.websocket) {
          return false;
        }

        const status = await this.waitForTransportDrain(this.websocket);
        return status === "drained";
      },
    });
  }

  private canTransitionLifecycle(next: LifecyclePhase) {
    return LIFECYCLE_TRANSITIONS[this.lifecyclePhase].includes(next);
  }

  private logLifecycle(
    event:
      | "phase_enter"
      | "phase_exit"
      | "phase_timeout"
      | "phase_error"
      | "phase_invalid",
    detail?: Record<string, unknown>
  ) {
    console.log("[WhisperLifecycle]", event, {
      fromState: this.lifecyclePhase,
      connectionState: this.connectionState,
      sessionId: this.latestConsultationStorageStatus?.sessionId ?? null,
      ...detail,
    });
  }

  private transitionLifecycle(next: LifecyclePhase, reason?: string) {
    const previous = this.lifecyclePhase;
    if (!this.canTransitionLifecycle(next)) {
      this.logLifecycle("phase_invalid", {
        attemptedTo: next,
        reason: reason ?? null,
      });
      return false;
    }

    this.logLifecycle("phase_exit", {
      fromState: previous,
      toState: next,
      reason: reason ?? null,
    });
    this.lifecyclePhase = next;
    this.logLifecycle("phase_enter", {
      fromState: previous,
      toState: next,
      reason: reason ?? null,
    });
    return true;
  }

  private canProcessAudioFrames() {
    return (
      this.lifecyclePhase === "idle" ||
      this.lifecyclePhase === "running_live" ||
      this.lifecyclePhase === "running_recovering"
    );
  }

  private async refreshConsultationStorageStatus(force = false) {
    if (!this.durableAudioSession) {
      return;
    }

    if (this.consultationStatusRefreshInFlight) {
      this.consultationStatusRefreshQueued = true;
      return;
    }

    const now = Date.now();
    const elapsedSinceLast = now - this.lastConsultationStatusRefreshAt;
    if (!force && elapsedSinceLast < CONSULTATION_STATUS_REFRESH_MIN_INTERVAL_MS) {
      if (this.consultationStatusRefreshTimer === null) {
        const delay = CONSULTATION_STATUS_REFRESH_MIN_INTERVAL_MS - elapsedSinceLast;
        this.consultationStatusRefreshTimer = window.setTimeout(() => {
          this.consultationStatusRefreshTimer = null;
          void this.refreshConsultationStorageStatus(true);
        }, delay);
      }
      return;
    }

    if (this.consultationStatusRefreshTimer !== null) {
      window.clearTimeout(this.consultationStatusRefreshTimer);
      this.consultationStatusRefreshTimer = null;
    }

    this.consultationStatusRefreshInFlight = true;

    try {
      const initializeIfMissing =
        this.lifecyclePhase !== "stopped" &&
        this.lifecyclePhase !== "stopping_freeze_input" &&
        this.lifecyclePhase !== "stopping_finalize_server" &&
        this.lifecyclePhase !== "stopping_cleanup" &&
        this.lifecyclePhase !== "failed";
      this.latestConsultationStorageStatus =
        await this.durableAudioSession.getConsultationStorageStatus({
          initializeIfMissing,
        });
      this.lastConsultationStatusRefreshAt = Date.now();
    } catch {
      this.latestConsultationStorageStatus = null;
    } finally {
      this.consultationStatusRefreshInFlight = false;
      if (this.consultationStatusRefreshQueued) {
        this.consultationStatusRefreshQueued = false;
        void this.refreshConsultationStorageStatus(true);
      }
    }

    if (
      this.lifecyclePhase === "stopped" &&
      this.batchReprocessStatus.state !== "running" &&
      this.latestConsultationStorageStatus === null
    ) {
      this.markBatchReprocessError(
        new BatchReprocessError("AUDIO_EXPIRED"),
        "expired"
      );
    }
  }

  private async maybeRunDurableReplay() {
    if (!this.replayOrchestrator || this.durableReplayInFlight) {
      return;
    }

    this.durableReplayInFlight = true;
    this.emitResilienceStatus();

    try {
      await this.replayOrchestrator.replayFromCommittedBoundary();
    } catch (error) {
      await this.durableAudioSession?.getDiagnostics();
      console.warn(this.toError(error, "Durable replay failed."));
    } finally {
      this.durableReplayInFlight = false;
      await this.refreshConsultationStorageStatus(true);
      this.emitResilienceStatus();
    }
  }

  private async readableStreamToBlob(
    stream: ReadableStream<Uint8Array>,
    contentType: string
  ): Promise<Blob> {
    const reader = stream.getReader();
    const chunks: ArrayBuffer[] = [];

    while (true) {
      const result = await reader.read();
      if (result.done) {
        break;
      }
      const chunk = result.value;
      const copy = new Uint8Array(chunk.byteLength);
      copy.set(chunk);
      chunks.push(copy.buffer);
    }

    return new Blob(chunks, { type: contentType });
  }

  private async uploadFinalArchiveIfConfigured() {
    if (!this.durableAudioSession || !this.finalUploadConfig?.endpoint) {
      return;
    }

    const format = this.finalUploadConfig.format ?? "pcm";
    const contentType = format === "wav" ? "audio/wav" : "application/octet-stream";
    const output = await this.durableAudioSession.buildFinalUpload(format);
    let body: BodyInit;

    if (output instanceof Blob) {
      body = output;
    } else if (this.finalUploadConfig.mode === "blob") {
      body = await this.readableStreamToBlob(output, contentType);
    } else {
      body = output as unknown as BodyInit;
    }

    if (typeof fetch !== "function") {
      throw new Error("Final upload requested but fetch API is unavailable.");
    }

    const response = await fetch(this.finalUploadConfig.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": contentType,
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`Final upload failed with status ${response.status}.`);
    }

    const uploadId =
      response.headers.get("x-upload-id") ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    await this.durableAudioSession.markArchiveCommitted(uploadId);
    await this.refreshConsultationStorageStatus(true);
  }

  async reprocessAudio<TPayload = unknown>(): Promise<TPayload> {
    const now = Date.now();
    const currentStatus = this.syncBatchReprocessStatus(now);

    if (currentStatus.state === "running") {
      throw new BatchReprocessError("REPROCESS_IN_PROGRESS");
    }

    if (currentStatus.state === "waiting_for_stop") {
      const error = new BatchReprocessError("SESSION_NOT_STOPPED");
      this.markBatchReprocessError(error, "waiting_for_stop");
      throw error;
    }

    if (currentStatus.state === "unconfigured") {
      const error = new BatchReprocessError("NOT_CONFIGURED");
      this.markBatchReprocessError(error, "unconfigured");
      throw error;
    }

    if (currentStatus.state === "expired") {
      const error = new BatchReprocessError("AUDIO_EXPIRED");
      this.markBatchReprocessError(error, "expired");
      throw error;
    }

    if (this.lifecyclePhase !== "stopped") {
      const error = new BatchReprocessError("SESSION_NOT_STOPPED");
      this.markBatchReprocessError(error);
      throw error;
    }

    if (!this.durableAudioSession) {
      const error = new BatchReprocessError("AUDIO_NOT_AVAILABLE");
      this.markBatchReprocessError(error, "expired");
      throw error;
    }

    this.setBatchReprocessStatus(
      createBatchReprocessStatus({
        state: "running",
        supported: true,
        configured: currentStatus.configured,
        available: false,
        expiresAt: currentStatus.expiresAt,
        lastStartedAt: now,
        lastCompletedAt: currentStatus.lastCompletedAt,
        lastError: null,
        now,
      })
    );

    let output: Blob | ReadableStream<Uint8Array>;
    try {
      output = await this.durableAudioSession.buildFinalUpload("wav");
    } catch (error) {
      const batchError =
        error instanceof Error &&
        (error.message === "No durable consultation audio available for export." ||
          error.message === "No durable session to export.")
          ? new BatchReprocessError("AUDIO_EXPIRED", { cause: error })
          : new BatchReprocessError("AUDIO_NOT_AVAILABLE", {
              cause: error,
              message: error instanceof Error ? error.message : undefined,
            });
      this.markBatchReprocessError(
        batchError,
        batchError.code === "AUDIO_EXPIRED" ? "expired" : "error"
      );
      throw batchError;
    }

    const wavBlob =
      output instanceof Blob
        ? output
        : await this.readableStreamToBlob(output, "audio/wav");

    if (typeof fetch !== "function") {
      const error = new BatchReprocessError("HTTP_ERROR", {
        message: "Batch reprocessing requested but fetch API is unavailable.",
      });
      this.markBatchReprocessError(error);
      throw error;
    }

    const formData = new FormData();
    formData.append("file", wavBlob, "consultation.wav");

    try {
      const response = await fetch(this.buildBatchReprocessUrl(), {
        method: "POST",
        headers: this.buildBatchReprocessHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const error = new BatchReprocessError("HTTP_ERROR", {
          status: response.status,
          message: `Batch reprocess failed with status ${response.status}.`,
        });
        this.markBatchReprocessError(error);
        throw error;
      }

      const payload = this.batchReprocessConfig?.parseResponse
        ? await this.batchReprocessConfig.parseResponse(response)
        : await response.json();
      const finishedAt = Date.now();
      const nextStatus = refreshBatchReprocessStatus(
        createBatchReprocessStatus({
          state: "ready",
          supported: true,
          configured: currentStatus.configured,
          available: true,
          expiresAt: currentStatus.expiresAt,
          lastStartedAt: now,
          lastCompletedAt: finishedAt,
          lastError: null,
          now: finishedAt,
        }),
        finishedAt
      );
      this.setBatchReprocessStatus(nextStatus);
      return payload as TPayload;
    } catch (error) {
      if (error instanceof BatchReprocessError) {
        throw error;
      }

      const batchError =
        error instanceof TypeError
          ? new BatchReprocessError("CORS_BLOCKED", { cause: error })
          : new BatchReprocessError("INVALID_RESPONSE", { cause: error });
      this.markBatchReprocessError(batchError);
      throw batchError;
    }
  }

  private buildBatchReprocessUrl(): string {
    const baseUrl =
      this.resolvedConnectionContext?.batchBaseUrl ??
      (typeof window !== "undefined" && window.location?.href
        ? window.location.href
        : "http://localhost/");
    const endpoint =
      this.batchReprocessConfig?.endpoint ??
      this.resolvedConnectionContext?.batchDefaultUrl;

    if (!endpoint) {
      throw new BatchReprocessError("NOT_CONFIGURED");
    }

    const batchUrl = new URL(endpoint, baseUrl);

    if (!this.resolvedConnectionContext?.realtimeUrl) {
      return batchUrl.toString();
    }

    const realtimeUrl = new URL(this.resolvedConnectionContext.realtimeUrl, baseUrl);
    realtimeUrl.searchParams.forEach((value, key) => {
      if (!batchUrl.searchParams.has(key)) {
        batchUrl.searchParams.set(key, value);
      }
    });

    return batchUrl.toString();
  }

  private buildBatchReprocessHeaders(): Record<string, string> | undefined {
    const headers = new Headers();

    Object.entries(this.resolvedConnectionContext?.headers ?? {}).forEach(([key, value]) => {
      if (typeof value === "string" && value.length > 0) {
        headers.set(key, value);
      }
    });

    headers.delete("Content-Type");

    if (
      this.resolvedConnectionContext?.authToken &&
      !headers.has("Authorization")
    ) {
      headers.set(
        "Authorization",
        `Bearer ${this.resolvedConnectionContext.authToken}`
      );
    }

    const output = Object.fromEntries(headers.entries());
    return Object.keys(output).length > 0 ? output : undefined;
  }

  public async ingestSttAudit(
    audit: DebugAuditFile
  ): Promise<SttAuditIngestionWarning | null> {
    if (!this.sttAuditIngestionConfig) {
      return null;
    }

    let endpoint: string;
    try {
      endpoint = this.buildSttAuditIngestionUrl();
    } catch (error) {
      return {
        code: "invalid_endpoint",
        message: "STT audit ingestion endpoint could not be resolved.",
        detail: {
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      };
    }

    if (typeof fetch !== "function") {
      return {
        code: "fetch_unavailable",
        message: "STT audit ingestion requested but fetch API is unavailable.",
        endpoint,
      };
    }

    const headerResult = this.buildSttAuditIngestionHeaders(endpoint);
    if (headerResult.warning) {
      return headerResult.warning;
    }

    const payload = this.buildSttAuditIngestionPayload(audit);
    const maxRetries = STT_AUDIT_INGESTION_RETRY_DELAYS_MS.length;
    let attempt = 1;

    while (true) {
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: headerResult.headers,
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          return null;
        }

        const shouldRetry = response.status >= 500 && attempt <= maxRetries;
        if (!shouldRetry) {
          return {
            code: "http_error",
            message: `STT audit ingestion failed with status ${response.status}.`,
            status: response.status,
            attempt,
            endpoint,
          };
        }
      } catch (error) {
        const shouldRetry = attempt <= maxRetries;
        if (!shouldRetry) {
          return {
            code: "network_error",
            message:
              "STT audit ingestion failed before receiving a server response.",
            attempt,
            endpoint,
            detail: {
              errorMessage: error instanceof Error ? error.message : String(error),
            },
          };
        }
      }

      const delayMs = STT_AUDIT_INGESTION_RETRY_DELAYS_MS[attempt - 1] ?? 0;
      attempt += 1;
      await this.waitForSttAuditRetry(delayMs);
    }
  }

  private buildSttAuditIngestionUrl(): string {
    const baseUrl =
      this.resolvedConnectionContext?.batchBaseUrl ??
      (typeof window !== "undefined" && window.location?.href
        ? window.location.href
        : "http://localhost/");
    const endpoint =
      this.sttAuditIngestionConfig?.endpoint ?? DEFAULT_STT_AUDIT_INGESTION_PATH;

    return new URL(endpoint, baseUrl).toString();
  }

  private buildSttAuditIngestionHeaders(endpoint: string): {
    headers?: Record<string, string>;
    warning?: SttAuditIngestionWarning;
  } {
    const headers = new Headers();

    Object.entries(this.resolvedConnectionContext?.headers ?? {}).forEach(([key, value]) => {
      if (typeof value === "string" && value.length > 0) {
        headers.set(key, value);
      }
    });

    Object.entries(this.sttAuditIngestionConfig?.headers ?? {}).forEach(([key, value]) => {
      if (typeof value === "string" && value.length > 0) {
        headers.set(key, value);
      }
    });

    if (
      this.resolvedConnectionContext?.authToken &&
      !headers.has("Authorization")
    ) {
      headers.set(
        "Authorization",
        `Bearer ${this.resolvedConnectionContext.authToken}`
      );
    }

    headers.set("Content-Type", "application/json");

    if (!headers.has("x-api-key")) {
      return {
        warning: {
          code: "missing_api_key",
          message:
            "STT audit ingestion skipped because header 'x-api-key' was not provided.",
          endpoint,
        },
      };
    }

    return {
      headers: Object.fromEntries(headers.entries()),
    };
  }

  private buildSttAuditIngestionPayload(
    audit: DebugAuditFile
  ): SttAuditIngestionPayload {
    const payload: SttAuditIngestionPayload = {
      audit,
    };

    const threadId = this.resolveSttAuditThreadId(audit);
    if (threadId) {
      payload.thread_id = threadId;
    }

    const externalIdentifier = this.resolveSttAuditExternalIdentifier(audit);
    if (externalIdentifier) {
      payload.external_identifier = externalIdentifier;
    }

    if (this.sttSessionIds.length > 0) {
      payload.stt_session_ids = [...this.sttSessionIds];
    }

    return payload;
  }

  private resolveSttAuditThreadId(audit: DebugAuditFile): string | undefined {
    return (
      this.asNonEmptyString(this.sttAuditIngestionConfig?.threadId) ??
      this.getAuditMetadataString(audit, "thread_id") ??
      this.getAuditMetadataString(audit, "threadId")
    );
  }

  private resolveSttAuditExternalIdentifier(
    audit: DebugAuditFile
  ): string | undefined {
    return (
      this.asNonEmptyString(this.sttAuditIngestionConfig?.externalIdentifier) ??
      this.asNonEmptyString(this.resolvedConnectionContext?.externalId) ??
      this.getAuditMetadataString(audit, "external_identifier") ??
      this.getAuditMetadataString(audit, "externalIdentifier") ??
      this.getAuditMetadataString(audit, "external_id")
    );
  }

  private getAuditMetadataString(
    audit: DebugAuditFile,
    key: string
  ): string | undefined {
    const metadata = audit.metadata;
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      return undefined;
    }

    return this.asNonEmptyString((metadata as Record<string, unknown>)[key]);
  }

  private asNonEmptyString(value: unknown): string | undefined {
    if (typeof value !== "string") {
      return undefined;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  private waitForSttAuditRetry(delayMs: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });
  }

  private static cancelSharedAudioContextIdleClose() {
    if (this.sharedAudioContextIdleCloseTimer === null) {
      return;
    }

    window.clearTimeout(this.sharedAudioContextIdleCloseTimer);
    this.sharedAudioContextIdleCloseTimer = null;
  }

  private static scheduleSharedAudioContextClose() {
    if (
      this.sharedAudioContextLeaseCount > 0 ||
      !this.sharedAudioContext ||
      this.sharedAudioContextIdleCloseTimer !== null
    ) {
      return;
    }

    this.sharedAudioContextIdleCloseTimer = window.setTimeout(() => {
      this.sharedAudioContextIdleCloseTimer = null;
      void this.closeSharedAudioContextImmediately();
    }, AUDIO_CONTEXT_IDLE_CLOSE_MS);
  }

  private static getSharedWorkletBlobUrl() {
    if (!this.sharedWorkletBlobUrl) {
      const blob = new Blob([REALTIME_AUDIO_WORKLET_CODE], {
        type: "application/javascript",
      });
      this.sharedWorkletBlobUrl = URL.createObjectURL(blob);
    }

    return this.sharedWorkletBlobUrl;
  }

  private static releaseSharedWorkletBlobUrl() {
    if (!this.sharedWorkletBlobUrl) {
      return;
    }

    URL.revokeObjectURL(this.sharedWorkletBlobUrl);
    this.sharedWorkletBlobUrl = null;
  }

  private static async closeSharedAudioContextImmediately() {
    this.cancelSharedAudioContextIdleClose();

    if (this.sharedAudioContextLeaseCount > 0 || !this.sharedAudioContext) {
      return;
    }

    const activeAudioContext = this.sharedAudioContext;
    this.sharedAudioContext = null;
    this.sharedWorkletModuleReadyPromise = null;
    this.releaseSharedWorkletBlobUrl();

    try {
      if (activeAudioContext.state === "running") {
        await activeAudioContext.suspend();
      }
      await activeAudioContext.close();
    } catch {
      // Ignore close errors during teardown.
    }
  }

  private async ensureWorkletModuleLoaded(audioContext: AudioContext) {
    if (!WhisperTranscriptionAdapter.sharedWorkletModuleReadyPromise) {
      WhisperTranscriptionAdapter.sharedWorkletModuleReadyPromise =
        audioContext.audioWorklet
          .addModule(WhisperTranscriptionAdapter.getSharedWorkletBlobUrl())
          .catch((error) => {
            WhisperTranscriptionAdapter.sharedWorkletModuleReadyPromise = null;
            throw error;
          });
    }

    await WhisperTranscriptionAdapter.sharedWorkletModuleReadyPromise;
  }

  private async restartPhaseForStart() {
    if (this.stopInFlightPromise) {
      try {
        await this.stopInFlightPromise;
      } catch {
        // Ignore stop failures; start should still attempt a clean bootstrap.
      }
    }

    if (this.terminalCleanupPromise) {
      try {
        await this.terminalCleanupPromise;
      } catch {
        // Ignore cleanup failures; start should still attempt a clean bootstrap.
      }
    }

    this.stopHealthChecks();
    this.removeBrowserNetworkListeners();
    this.detachAudioPipeline();
    this.stopMediaStreamTracks();
    this.tempPartialTranscription = undefined;
    this.connectionSequence = 0;
    this.currentSttSessionId = null;
    this.sttSessionIds = [];
    this.durableReplayInFlight = false;
    this.finishDeliveryState = null;
    this.stopFinalizationState = null;

    if (this.websocket) {
      this.closeWebSocket();
    }

    await this.closeAudioContext();
    this.transitionLifecycle("starting_bootstrap", "preflight_completed");
  }

  private releaseAudioContextLease() {
    if (!this.hasAudioContextLease) {
      this.audioContext = null;
      return;
    }

    this.hasAudioContextLease = false;
    WhisperTranscriptionAdapter.sharedAudioContextLeaseCount = Math.max(
      0,
      WhisperTranscriptionAdapter.sharedAudioContextLeaseCount - 1
    );
    this.audioContext = null;
  }

  public static async __resetSharedAudioRuntimeForTests() {
    this.cancelSharedAudioContextIdleClose();
    this.sharedAudioContextLeaseCount = 0;
    await this.closeSharedAudioContextImmediately();
    this.sharedWorkletModuleReadyPromise = null;
    this.releaseSharedWorkletBlobUrl();
  }

  public async startTranscription(mediaStream: MediaStream): Promise<void> {
    if (!mediaStream) {
      console.error("No mediaStream was received.");
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      console.error("getUserMedia not supported on your browser!");
      return;
    }

    this.transitionLifecycle("starting_preflight", "start_called");
    await this.restartPhaseForStart();
    this.mediaStream = mediaStream;
    this.intentionalStop = false;
    this.finishDeliveryState = null;
    this.stopFinalizationState = null;
    this.awaitingReconnection = false;
    this.pendingDisconnectDetails = null;
    this.lastDisconnectDetails = null;
    this.currentReconnectAttempt = null;
    this.nextReconnectDelayMs = null;
    this.connectionState = "connecting";
    this.metricsCollector.resetSession();
    this.metricsCollector.markSessionStart();
    this.lastProducedSeq = 0;
    this.latestConsultationStorageStatus = null;
    this.clearBatchReprocessExpiryTimer();
    this.setBatchReprocessStatus(this.createWaitingBatchReprocessStatus());
    this.bindBrowserNetworkListeners();
    this.startHealthChecks();
    this.ensureWebSocketForStart();
    if (this.websocket?.readyState === ReconnectingWebSocket.OPEN) {
      this.connectionState = "connected";
    }
    this.emitResilienceStatus();
    this.setupDurableRuntime();
    await this.durableAudioSession?.startSession();
    await this.refreshConsultationStorageStatus();

    try {
      const audioContext = this.ensureAudioContext();
      if (
        typeof audioContext.resume === "function" &&
        audioContext.state !== "running"
      ) {
        await audioContext.resume();
      }

      this.input = audioContext.createMediaStreamSource(this.mediaStream);

      if (!this.input) {
        throw new Error("Unable to createMediaStreamSource.");
      }

      this.recordingNode = await this.setupRecordingWorkletNode();

      this.recordingNode.port.onmessage = (event) => {
        if (this.recordState !== "PAUSED") {
          this.postMessage(event.data);
        }
      };
      this.input.connect(this.recordingNode);
      this.recordState = "RECORDING";
      this.transitionLifecycle("running_live", "audio_pipeline_ready");
      this.metricsCollector.recordResilienceStatus(this.getResilienceStatus());
    } catch (error) {
      this.recordState = "PAUSED";
      this.input?.disconnect();
      this.input = undefined;
      this.recordingNode = undefined;
      await this.closeAudioContext();
      this.metricsCollector.markSessionError(true);

      const transcriptionError = this.toError(
        error,
        "Failed to start Whisper transcription."
      );
      this.transitionLifecycle("failed", "start_bootstrap_failed");
      this.logLifecycle("phase_error", {
        phase: "starting_bootstrap",
        errorMessage: transcriptionError.message,
      });
      console.error(transcriptionError);
    }
  }

  private ensureAudioContext() {
    WhisperTranscriptionAdapter.cancelSharedAudioContextIdleClose();

    if (!WhisperTranscriptionAdapter.sharedAudioContext) {
      WhisperTranscriptionAdapter.sharedAudioContext = new AudioContext({
        sampleRate: 44100,
      });
      WhisperTranscriptionAdapter.sharedWorkletModuleReadyPromise = null;
    }

    if (!this.hasAudioContextLease) {
      this.hasAudioContextLease = true;
      WhisperTranscriptionAdapter.sharedAudioContextLeaseCount += 1;
    }

    this.audioContext = WhisperTranscriptionAdapter.sharedAudioContext;
    return this.audioContext;
  }

  async setupRecordingWorkletNode() {
    const audioContext = this.ensureAudioContext();
    await this.ensureWorkletModuleLoaded(audioContext);
    return new AudioWorkletNode(audioContext, REALTIME_AUDIO_PROCESSOR_NAME);
  }

  private postMessage = (sampleData: Float32Array) => {
    if (!this.canProcessAudioFrames()) {
      return;
    }

    this.metricsCollector.recordAudioFrame(sampleData);

    const activeWebSocket = this.websocket;

    if (!activeWebSocket) {
      return;
    }

    const outputSampleRate = 16000;
    const decreaseResultBuffer = this.decreaseSampleRate(
      sampleData,
      44100,
      outputSampleRate
    );

    if (!decreaseResultBuffer) {
      return;
    }

    const audioPayload = this.convertFloat32ToInt16(decreaseResultBuffer);
    this.metricsCollector.markAudioChunkProduced(audioPayload.byteLength);
    const holdForRecoveryReplay = this.shouldBufferOfflineAudio();

    if (this.durableAudioSession) {
      const durationMs = this.getAudioPayloadDurationMs(audioPayload.byteLength);
      void this.durableAudioSession
        .appendCaptureChunk(audioPayload, durationMs)
        .then((seq) => {
          this.lastProducedSeq = seq;
          if (holdForRecoveryReplay || seq % 10 === 0) {
            void this.refreshConsultationStorageStatus();
          }
        })
        .catch((error) => {
          console.error(
            this.toError(error, "Failed to persist durable audio capture chunk.")
          );
        });
    }

    if (this.connectionState === "disconnected") {
      this.metricsCollector.markAudioChunkDropped(
        audioPayload.byteLength,
        "terminal_disconnected"
      );
      return;
    }

    if (holdForRecoveryReplay) {
      return;
    }

    if (
      activeWebSocket.readyState === ReconnectingWebSocket.OPEN &&
      activeWebSocket.bufferedAmount >
        this.resilienceConfig.maxBufferedAmountBytes
    ) {
      this.reconnectTransport(
        "WebSocket upstream stalled and stopped draining audio."
      );
      return;
    }

    try {
      activeWebSocket.send(audioPayload);
      this.metricsCollector.markAudioChunkSent(audioPayload.byteLength);
      if (this.durableAudioSession && this.lastProducedSeq > 0) {
        void this.durableAudioSession.markLiveSentThroughSeq(this.lastProducedSeq);
      }

      if (
        activeWebSocket.readyState === ReconnectingWebSocket.OPEN &&
        activeWebSocket.bufferedAmount >
          this.resilienceConfig.maxBufferedAmountBytes
      ) {
        this.reconnectTransport(
          "WebSocket upstream stalled and buffered audio above the safety threshold."
        );
      }
    } catch (error) {
      const connectionError =
        error instanceof Error
          ? error
          : new Error("Failed to send audio to the transcription WebSocket.");
      console.warn(connectionError);
      this.reconnectTransport(
        "Failed to send audio to the transcription WebSocket.",
        connectionError
      );
    }
  };

  private bindWebsocket = (ws: ReconnectingWebSocket) => {
    console.count("whisper:bind_websocket");
    this.websocket = ws;

    ws.onopen = () => {
      this.connectionSequence += 1;
      if (this.intentionalStop) {
        this.logStopFlow("ws_onopen_during_stop");
        this.trySendStopFinish();
        return;
      }

      this.metricsCollector.markReady();
      this.connectionState = "connected";
      this.currentReconnectAttempt = null;
      this.nextReconnectDelayMs = null;
      this.emit("connected");
      if (this.hasConnectedOnce && this.awaitingReconnection) {
        this.emit("reconnected");
      }

      this.hasConnectedOnce = true;
      this.awaitingReconnection = false;
      this.lastReconnectAttempt = 0;
      this.pendingDisconnectDetails = null;
      this.suppressNextReconnectEvent = false;
      this.emitResilienceStatus();
      if (this.lifecyclePhase === "paused") {
        this.transitionLifecycle("paused", "socket_open_while_paused");
      } else if (
        this.lifecyclePhase === "starting_bootstrap" ||
        this.lifecyclePhase === "running_recovering" ||
        this.lifecyclePhase === "running_live"
      ) {
        this.transitionLifecycle("running_live", "socket_open");
      }
      void this.maybeRunDurableReplay();
    };

    ws.onclose = (event) => {
      if (this.intentionalStop) {
        this.logStopFlow("ws_onclose_during_stop", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        this.maybeHandleStopFinalizationOnClose();
        return;
      }

      const details = this.pendingDisconnectDetails || {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      };
      this.lastDisconnectDetails = details;

      // Uma rejeição de autenticação não se resolve com nova tentativa: o
      // servidor recusaria a mesma chave indefinidamente.
      if (event.code === AUTH_REJECTED_CLOSE_CODE) {
        this.handleAuthRejection(details);
        return;
      }

      if (this.isTerminalDisconnect()) {
        if (this.isBrowserOffline()) {
          this.awaitingReconnection = true;
          this.connectionState = "reconnecting";
          this.currentReconnectAttempt = this.getRetryCount();
          this.nextReconnectDelayMs = null;
          this.pendingDisconnectDetails = null;
          this.suppressNextReconnectEvent = false;
          this.emitResilienceStatus();
          return;
        }

        this.awaitingReconnection = false;
        this.connectionState = "disconnected";
        this.currentReconnectAttempt = this.getRetryCount();
        this.nextReconnectDelayMs = null;
        this.emit("disconnected", details);
        this.transitionLifecycle("failed", "terminal_disconnect");
        this.scheduleTerminalCleanup();
      } else {
        this.awaitingReconnection = true;
        const reconnectEventDetails = this.getReconnectEventDetails();
        this.connectionState = "reconnecting";
        this.transitionLifecycle("running_recovering", "transport_reconnecting");
        this.currentReconnectAttempt = reconnectEventDetails.attempt;
        this.nextReconnectDelayMs = reconnectEventDetails.delay;

        if (!this.suppressNextReconnectEvent) {
          this.lastReconnectAttempt = reconnectEventDetails.attempt;
          this.emit("reconnecting", reconnectEventDetails);
        }
      }

      this.pendingDisconnectDetails = null;
      this.suppressNextReconnectEvent = false;
      void this.durableAudioSession?.incrementReconnectCount();
      void this.refreshConsultationStorageStatus();
      this.emitResilienceStatus();
    };

    ws.onerror = (event) => {
      if (this.intentionalStop) {
        return;
      }

      const errorMessage =
        "message" in event && typeof event.message === "string"
          ? event.message
          : "WebSocket transport reported an error.";

      this.pendingDisconnectDetails = {
        ...this.pendingDisconnectDetails,
        error: new Error(errorMessage),
      };
    };

    ws.onmessage = (event: MessageEvent) => {
      let transcriptData;

      try {
        if (typeof event.data !== "string") {
          throw new Error(
            "Whisper websocket message payload must be a JSON string."
          );
        }

        transcriptData = JSON.parse(event.data);
      } catch (error) {
        console.error(
          this.toError(error, "Failed to parse Whisper websocket message.")
        );
        return;
      }
      // Só observa os campos de sessão; a mensagem segue o fluxo normal.
      this.trackSttSession(transcriptData);
      if (this.intentionalStop || this.stopFinalizationState) {
        const payload = transcriptData as {
          is_partial?: boolean;
          status?: string;
          data?: { text?: string };
        };
        this.logStopFlow("ws_onmessage_during_stop", {
          isPartial: payload?.is_partial ?? null,
          status:
            typeof payload?.status === "string"
              ? payload.status.toLowerCase()
              : null,
          hasText:
            typeof payload?.data?.text === "string" &&
            payload.data.text.trim().length > 0,
        });
        // During stop finalization, avoid extra transcript merge/storage work.
        this.maybeHandleStopFinalizationOnMessage(transcriptData);
        return;
      }

      this.handleParsedTranscriptMessage(transcriptData);
    };
  };

  private startHealthChecks() {
    console.count("whisper:start_health_checks");
    if (this.healthCheckTimer) {
      console.count("whisper:start_health_checks_skipped_existing_timer");
      return;
    }

    this.healthCheckTimer = window.setInterval(() => {
      const activeWebSocket = this.websocket;

      if (
        this.intentionalStop ||
        this.durableReplayInFlight ||
        !activeWebSocket ||
        activeWebSocket.readyState !== ReconnectingWebSocket.OPEN
      ) {
        return;
      }

      if (
        activeWebSocket.bufferedAmount >
        this.resilienceConfig.maxBufferedAmountBytes
      ) {
        this.reconnectTransport(
          "WebSocket health check detected sustained backpressure."
        );
      }
    }, this.resilienceConfig.healthCheckIntervalMs);
  }

  private stopHealthChecks() {
    console.count("whisper:stop_health_checks");
    if (!this.healthCheckTimer) {
      console.count("whisper:stop_health_checks_skipped_no_timer");
      return;
    }

    window.clearInterval(this.healthCheckTimer);
    this.healthCheckTimer = null;
  }

  private detachAudioPipeline() {
    console.count("whisper:detach_audio_pipeline");
    if (this.recordingNode?.port) {
      try {
        this.recordingNode.port.postMessage({ type: "sofya-stop" });
      } catch {
        // Ignore port messaging errors during teardown.
      }

      this.recordingNode.port.onmessage = null;
      if ("close" in this.recordingNode.port) {
        try {
          this.recordingNode.port.close();
        } catch {
          // Ignore port close errors during teardown.
        }
      }
    }

    if (this.input) {
      try {
        this.input.disconnect();
      } catch {
        // Ignore disconnect errors during teardown.
      }
    }

    if (this.recordingNode && "disconnect" in this.recordingNode) {
      try {
        (this.recordingNode as AudioNode).disconnect();
      } catch {
        // Ignore disconnect errors during teardown.
      }
    }

    this.input = undefined;
    this.recordingNode = undefined;
    this.recordState = "PAUSED";
  }

  private stopMediaStreamTracks() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }

  private async closeAudioContext(forceImmediateClose = false) {
    this.releaseAudioContextLease();
    this.audioContext = null;

    if (forceImmediateClose) {
      await WhisperTranscriptionAdapter.closeSharedAudioContextImmediately();
      return;
    }

    WhisperTranscriptionAdapter.scheduleSharedAudioContextClose();
  }

  private closeWebSocket() {
    console.count("whisper:close_websocket");
    if (!this.websocket) {
      console.count("whisper:close_websocket_skipped_no_socket");
      return;
    }

    const activeWebSocket = this.websocket;
    this.websocket = null;
    activeWebSocket.onopen = null;
    activeWebSocket.onclose = null;
    activeWebSocket.onmessage = null;
    activeWebSocket.onerror = null;
    activeWebSocket.close();
  }

  private async releaseRuntimeResources(
    forceAudioContextClose = false
  ) {
    this.intentionalStop = true;
    this.stopHealthChecks();
    this.removeBrowserNetworkListeners();
    this.detachAudioPipeline();
    this.stopMediaStreamTracks();
    this.tempPartialTranscription = undefined;
    this.durableReplayInFlight = false;
    if (this.consultationStatusRefreshTimer !== null) {
      window.clearTimeout(this.consultationStatusRefreshTimer);
      this.consultationStatusRefreshTimer = null;
    }
    this.consultationStatusRefreshQueued = false;
    this.closeWebSocket();
    await this.closeAudioContext(forceAudioContextClose);
  }

  private scheduleTerminalCleanup() {
    if (this.terminalCleanupPromise || this.intentionalStop) {
      return;
    }

    this.terminalCleanupPromise = Promise.resolve()
      .then(async () => {
        await this.releaseRuntimeResources(true);
        this.metricsCollector.dispose();
      })
      .finally(() => {
        this.terminalCleanupPromise = null;
      });
  }

  private isBrowserOffline() {
    return !this.browserOnline;
  }

  private bindBrowserNetworkListeners() {
    console.count("whisper:bind_browser_network_listeners");
    if (typeof window === "undefined" || !window.addEventListener) {
      console.count("whisper:bind_browser_network_listeners_skipped_no_window");
      return;
    }

    window.addEventListener("online", this.handleBrowserOnline);
    window.addEventListener("offline", this.handleBrowserOffline);
  }

  private removeBrowserNetworkListeners() {
    console.count("whisper:remove_browser_network_listeners");
    if (typeof window === "undefined" || !window.removeEventListener) {
      console.count("whisper:remove_browser_network_listeners_skipped_no_window");
      return;
    }

    window.removeEventListener("online", this.handleBrowserOnline);
    window.removeEventListener("offline", this.handleBrowserOffline);
  }

  private reconnectTransport(reason: string, error?: Error) {
    console.count("whisper:reconnect_transport_called");
    const activeWebSocket = this.websocket;

    if (!activeWebSocket || this.intentionalStop) {
      console.count("whisper:reconnect_transport_skipped_inactive_or_stopping");
      return;
    }

    if (this.awaitingReconnection || this.connectionState === "reconnecting") {
      console.count("whisper:reconnect_transport_skipped_already_reconnecting");
      return;
    }

    this.pendingDisconnectDetails = {
      reason,
      error,
    };
    this.lastDisconnectDetails = {
      reason,
      error,
    };
    this.awaitingReconnection = true;
    this.lastReconnectAttempt = 1;
    this.suppressNextReconnectEvent = true;
    this.connectionState = "reconnecting";
    this.transitionLifecycle("running_recovering", "manual_reconnect_transport");
    this.currentReconnectAttempt = 1;
    this.nextReconnectDelayMs = 0;
    this.emit("reconnecting", {
      attempt: 1,
      delay: 0,
    });
    this.emitResilienceStatus();
    activeWebSocket.reconnect(4000, reason);
  }

  private shouldBufferOfflineAudio() {
    const activeWebSocket = this.websocket;

    return (
      this.durableReplayInFlight ||
      this.awaitingReconnection ||
      !activeWebSocket ||
      activeWebSocket.readyState !== ReconnectingWebSocket.OPEN
    );
  }

  private async waitForTransportDrain(
    activeWebSocket: ReconnectingWebSocket
  ): Promise<DrainExitReason | "drained"> {
    let previousBufferedAmount = activeWebSocket.bufferedAmount;
    let stagnantMs = 0;

    while (!this.intentionalStop) {
      if (activeWebSocket.readyState !== ReconnectingWebSocket.OPEN) {
        return "socket_unavailable";
      }

      if (activeWebSocket.bufferedAmount === 0) {
        return "drained";
      }

      await this.delay(TRANSPORT_DRAIN_POLL_MS);

      if (activeWebSocket.bufferedAmount < previousBufferedAmount) {
        stagnantMs = 0;
      } else {
        stagnantMs += TRANSPORT_DRAIN_POLL_MS;
      }

      previousBufferedAmount = activeWebSocket.bufferedAmount;

      if (stagnantMs >= this.resilienceConfig.drainStallTimeoutMs) {
        this.reconnectTransport("Offline audio drain stalled.");
        return "stalled";
      }
    }

    return "stopped";
  }

  private delay(milliseconds: number) {
    return new Promise<void>((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });
  }

  private requestPersistentStorage() {
    if (!navigator.storage?.persist) {
      return;
    }

    void navigator.storage.persist().catch(() => undefined);
  }

  private getReconnectEventDetails() {
    const attempt = this.getRetryCount() || 1;

    return {
      attempt,
      delay: this.getReconnectDelay(attempt),
    };
  }

  private handleAuthRejection(details: IDisconnectDetails) {
    const reason = details.reason ? ` (${details.reason})` : "";
    const error = new SofyaAuthError(
      "AUTH_REJECTED",
      `The transcription server rejected the API key${reason}. ` +
        "Check the configured auth.key and its transport."
    );

    try {
      // Impede que a camada de resiliência reabra a conexão com a mesma chave.
      this.websocket?.close();
    } catch {
      // ignore
    }

    this.awaitingReconnection = false;
    this.pendingDisconnectDetails = null;
    this.suppressNextReconnectEvent = false;
    this.connectionState = "disconnected";
    this.currentReconnectAttempt = this.getRetryCount();
    this.nextReconnectDelayMs = null;
    this.emit("error", error);
    this.emit("disconnected", details);
    this.transitionLifecycle("failed", "auth_rejected");
    this.scheduleTerminalCleanup();
    this.emitResilienceStatus();
  }

  private isTerminalDisconnect() {
    if (!this.websocket) {
      return true;
    }

    const maxRetries = this.getMaxRetries();
    const retryCount = this.getRetryCount();

    return (
      Number.isFinite(maxRetries) &&
      retryCount >= maxRetries &&
      retryCount === this.lastReconnectAttempt
    );
  }

  private getRetryCount() {
    return (this.websocket as any)?.retryCount ?? 0;
  }

  private getMaxRetries() {
    return (
      (this.websocket as any)?._options?.maxRetries ??
      this.resilienceConfig.maxReconnectAttempts
    );
  }

  private getWebSocketState(): ResilienceWebSocketState {
    if (!this.websocket) {
      return "unavailable";
    }

    switch (this.websocket.readyState) {
      case ReconnectingWebSocket.CONNECTING:
        return "connecting";
      case ReconnectingWebSocket.OPEN:
        return "open";
      case ReconnectingWebSocket.CLOSING:
        return "closing";
      case ReconnectingWebSocket.CLOSED:
        return "closed";
      default:
        return "unavailable";
    }
  }

  private getRemainingReconnectAttempts() {
    const maxRetries = this.getMaxRetries();

    if (!Number.isFinite(maxRetries)) {
      return null;
    }

    return Math.max(maxRetries - this.getRetryCount(), 0);
  }

  private emitResilienceStatus() {
    const status = this.getResilienceStatus();
    this.metricsCollector.recordResilienceStatus(status);
    this.emit("resilience_status", status);
  }

  private getBufferedAudioBytes() {
    return this.latestConsultationStorageStatus?.backlogBytes ?? 0;
  }

  public getResilienceStatus() {
    const durableStatus = this.latestConsultationStorageStatus;
    const pendingBufferedAudioBytes = 0;
    const pendingBufferedAudioChunks = 0;
    const totalBufferedAudioBytes = this.getBufferedAudioBytes();
    const persistedBufferedAudioBytes = totalBufferedAudioBytes;
    const persistedBufferedAudioSegments = durableStatus?.durableSegments ?? 0;

    return {
      connectionState: this.connectionState,
      websocketState: this.getWebSocketState(),
      browserOnline: this.browserOnline,
      hasConnectedOnce: this.hasConnectedOnce,
      isRecovering: this.connectionState === "reconnecting",
      isBufferingAudio: totalBufferedAudioBytes > 0,
      isDrainingAudio: this.durableReplayInFlight,
      transportBufferedAmountBytes: this.websocket?.bufferedAmount ?? 0,
      pendingBufferedAudioBytes,
      pendingBufferedAudioChunks,
      persistedBufferedAudioBytes,
      persistedBufferedAudioSegments,
      totalBufferedAudioBytes,
      bufferingStartedAt: null,
      drainStartedAt: null,
      lastDrainProgressAt: null,
      drainCycles: 0,
      drainExitReason: null,
      reconnectAttempt: this.currentReconnectAttempt,
      nextReconnectDelayMs: this.nextReconnectDelayMs,
      finishDeliveryState: this.finishDeliveryState,
      maxReconnectAttempts: this.getMaxRetries(),
      remainingReconnectAttempts: this.getRemainingReconnectAttempts(),
      lastDisconnect: this.lastDisconnectDetails
        ? {
            code: this.lastDisconnectDetails.code,
            reason: this.lastDisconnectDetails.reason,
            wasClean: this.lastDisconnectDetails.wasClean,
            errorMessage: this.lastDisconnectDetails.error?.message,
          }
        : null,
    };
  }

  public getTelemetrySnapshot(): TelemetrySnapshot {
    return this.metricsCollector.getTelemetrySnapshot();
  }

  public getTelemetryRows(): TelemetryRow[] {
    return this.metricsCollector.getTelemetryRows();
  }

  public clearTelemetryRows(): void {
    this.metricsCollector.clearTelemetryRows();
  }

  public resetTelemetry(): void {
    this.metricsCollector.resetTelemetry();
  }

  private getReconnectDelay(attempt: number) {
    return Math.min(
      this.resilienceConfig.reconnectBaseDelayMs *
        Math.pow(
          this.resilienceConfig.reconnectDelayGrowFactor,
          Math.max(attempt - 1, 0)
        ),
      this.resilienceConfig.reconnectMaxDelayMs
    );
  }

  private toError(error: unknown, fallbackMessage: string) {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === "string" && error.trim().length > 0) {
      return new Error(error);
    }

    return new Error(fallbackMessage);
  }

  private logStopFlow(step: string, detail?: Record<string, unknown>) {
    const stopState = this.stopFinalizationState;
    console.log("[WhisperStopFlow]", step, {
      connectionState: this.connectionState,
      websocketState: this.getWebSocketState(),
      websocketReadyState: this.websocket?.readyState ?? null,
      finishDeliveryState: this.finishDeliveryState,
      stopInFlight: this.stopInFlightPromise !== null,
      stopAwaitingAck: !!stopState,
      stopFinishSent: stopState?.finishSent ?? null,
      stopSettled: stopState?.settled ?? null,
      ...detail,
    });
  }

  private settleStopFinalization() {
    const state = this.stopFinalizationState;

    if (!state || state.settled) {
      this.logStopFlow("settle_skip", {
        reason: !state ? "no_stop_state" : "already_settled",
      });
      return;
    }

    this.logStopFlow("settle_start");
    state.settled = true;
    window.clearTimeout(state.timeoutId);
    this.stopFinalizationState = null;
    this.logStopFlow("settle_resolve");
    state.resolve();
  }

  private emitPartialFallbackAsFinalIfPresent() {
    if (!this.tempPartialTranscription?.length) {
      this.logStopFlow("fallback_skip_no_partial");
      return;
    }

    this.logStopFlow("fallback_emit_partial_as_final", {
      partialLength: this.tempPartialTranscription.length,
    });
    this.metricsCollector.recordTranscriptFinal(this.tempPartialTranscription);
    this.emit("recognized", this.tempPartialTranscription);
    this.tempPartialTranscription = undefined;
  }

  private trySendStopFinish() {
    const stopState = this.stopFinalizationState;

    if (
      !stopState ||
      stopState.finishSent ||
      !this.websocket ||
      this.websocket.readyState !== ReconnectingWebSocket.OPEN
    ) {
      this.logStopFlow("finish_send_skip", {
        hasStopState: !!stopState,
        finishAlreadySent: stopState?.finishSent ?? null,
        hasWebsocket: !!this.websocket,
      });
      return;
    }

    this.logStopFlow("finish_send_start");
    stopState.finishSent = true;
    this.finishDeliveryState = "sent";
    this.emitResilienceStatus();
    this.websocket.send(JSON.stringify({ action: "finish" }));
    this.logStopFlow("finish_send_done");
  }

  private maybeHandleStopFinalizationOnClose() {
    const stopState = this.stopFinalizationState;

    if (!stopState) {
      this.logStopFlow("onclose_stop_no_state");
      return;
    }

    this.logStopFlow("onclose_stop_handle", {
      finishSent: stopState.finishSent,
    });
    if (!stopState.finishSent) {
      this.finishDeliveryState = "skipped_socket_closed";
      this.emitResilienceStatus();
    }

    this.emitPartialFallbackAsFinalIfPresent();
    this.settleStopFinalization();
  }

  private maybeHandleStopFinalizationOnMessage(data: unknown) {
    const stopState = this.stopFinalizationState;

    if (!stopState) {
      return;
    }

    const payload = data as {
      is_partial?: boolean;
      status?: string;
      data?: { text?: string };
    };
    const terminal = this.isTerminalStopMessage(data, stopState.finishSent);
    this.logStopFlow("onmessage_stop_check", {
      isPartial: payload?.is_partial ?? null,
      status:
        typeof payload?.status === "string" ? payload.status.toLowerCase() : null,
      hasText:
        typeof payload?.data?.text === "string" &&
        payload.data.text.trim().length > 0,
      terminal,
    });

    if (terminal) {
      this.handleStopTerminalTranscriptPayload(payload);
      this.settleStopFinalization();
    }
  }

  private handleStopTerminalTranscriptPayload(payload: {
    is_partial?: boolean;
    status?: string;
    data?: { text?: string };
  }) {
    if (payload?.is_partial !== false) {
      return;
    }

    const transcriptText =
      typeof payload?.data?.text === "string" ? payload.data.text.trim() : "";

    if (transcriptText.length > 0) {
      this.metricsCollector.recordTranscriptFinal(transcriptText);
      this.emit("recognized", transcriptText);
      if (this.tempPartialTranscription?.length) {
        this.emit("recognizing", "");
      }
      this.tempPartialTranscription = undefined;
      return;
    }

    this.emitPartialFallbackAsFinalIfPresent();
  }

  private isTerminalStopMessage(data: unknown, finishSent = false): boolean {
    const payload = data as {
      is_partial?: boolean;
      status?: string;
      data?: { text?: string };
    };

    if (payload?.is_partial === true) {
      return false;
    }

    if (finishSent && payload?.is_partial === false) {
      return true;
    }

    const status =
      typeof payload?.status === "string" ? payload.status.toLowerCase() : "";
    const hasText =
      typeof payload?.data?.text === "string" &&
      payload.data.text.trim().length > 0;

    return status === "final" || hasText;
  }

  private handleParsedTranscriptMessage(transcriptData: any) {
    void this.durableAudioSession?.onTranscriptMessage(transcriptData);
    void this.refreshConsultationStorageStatus();

    const isPartial = transcriptData?.is_partial === true;
    const transcriptText =
      typeof transcriptData?.data?.text === "string"
        ? transcriptData.data.text
        : "";

    if (!isPartial) {
      let recognizedText: string | undefined;
      const status =
        typeof transcriptData?.status === "string"
          ? transcriptData.status.toLowerCase()
          : "";

      if (transcriptText.trim().length > 0) {
        if (this.durableAudioSession) {
          const merged = this.durableAudioSession.mergeTranscriptMessage(
            transcriptData
          );
          recognizedText = merged.recognizedText;
          if (
            !recognizedText &&
            this.intentionalStop &&
            status === "final" &&
            this.tempPartialTranscription?.trim().length
          ) {
            recognizedText = transcriptText.trim();
          }
        } else {
          recognizedText = transcriptText;
        }
      } else if (this.tempPartialTranscription?.trim().length) {
        // Some final events do not include text; preserve the last partial as final.
        recognizedText = this.tempPartialTranscription.trim();
      }

      if (recognizedText) {
        this.metricsCollector.recordTranscriptFinal(recognizedText);
        this.emit("recognized", recognizedText);
      }

      if (this.tempPartialTranscription?.length) {
        // Ensure consumer UIs can clear any in-flight partial caption.
        this.emit("recognizing", "");
      }
      this.tempPartialTranscription = undefined;
    }
    if (!isPartial && transcriptData?.data?.speakers) {
      this.metricsCollector.recordTranscriptDiarization();
      this.emit("recognized_diarization", transcriptData?.data?.speakers);
    }
    if (isPartial && transcriptText.trim().length > 0) {
      const merged = this.durableAudioSession
        ? this.durableAudioSession.mergeTranscriptMessage(transcriptData)
        : { recognizingText: transcriptText };
      const recognizingText = merged.recognizingText ?? transcriptText;
      this.tempPartialTranscription = recognizingText;
      this.metricsCollector.recordTranscriptPartial(recognizingText);
      this.emit("recognizing", recognizingText);
    }
  }

  private trackSttSession(payload: unknown) {
    const fields = payload as SttSessionFields | null;
    const sttSessionId =
      typeof fields?.session_id === "string" ? fields.session_id.trim() : "";

    // Servidor antigo (sem o campo) ou mesmo id repetido: nada a fazer.
    if (!sttSessionId || sttSessionId === this.currentSttSessionId) {
      return;
    }

    this.currentSttSessionId = sttSessionId;
    this.sttSessionIds.push(sttSessionId);

    const info: WhisperSttSessionInfo = {
      sttSessionId,
      externalId:
        this.asNonEmptyString(this.resolvedConnectionContext?.externalId) ?? null,
      // `onopen` sempre precede a mensagem; o `max` só protege o tipo (>= 1).
      connectionAttempt: Math.max(this.connectionSequence, 1),
    };
    this.emit("stt_session", info);
  }

  private languageSelector = (language: string) => {
    switch (language) {
      case "pt-BR":
        return "portuguese";
      case "en-US":
        return "english";
      case "es-ES":
        return "spanish";
      default:
        return "multilingual";
    }
  };

  private sendAudioConfig = (language = "multilingual") => {
    let processingArgs = {};
    const chunk_length_seconds = "3";
    const chunk_offset_seconds = "0.1";
    let selectedStrategy = "silence_at_end_of_chunk";
    if (selectedStrategy === "silence_at_end_of_chunk") {
      processingArgs = {
        chunk_length_seconds: parseFloat(chunk_length_seconds),
        chunk_offset_seconds: parseFloat(chunk_offset_seconds),
      };
    }

    const audioConfig = {
      type: "config",
      data: {
        sampleRate: 44100,
        channels: 1,
        language: this.languageSelector(language),
        processing_strategy: selectedStrategy,
        processing_args: processingArgs,
      },
    };

    if (
      this.websocket &&
      this.websocket.readyState === ReconnectingWebSocket.OPEN
    ) {
      this.websocket.send(JSON.stringify(audioConfig));
    }
  };

  private decreaseSampleRate(
    buffer: Float32Array,
    inputSampleRate: number,
    outputSampleRate: number
  ): Float32Array | undefined {
    if (inputSampleRate < outputSampleRate) {
      console.error("Sample rate too small.");
      return undefined;
    } else if (inputSampleRate === outputSampleRate) {
      return buffer;
    }

    const sampleRateRatio = inputSampleRate / outputSampleRate;
    const newLength = Math.ceil(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0;
      let count = 0;
      for (
        let i = offsetBuffer;
        i < nextOffsetBuffer && i < buffer.length;
        i++
      ) {
        accum += buffer[i];
        count++;
      }
      result[offsetResult] = accum / count;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }
    return result;
  }

  private convertFloat32ToInt16(buffer: Float32Array) {
    let length = buffer.length;
    const convertedBuffer = new Int16Array(length);
    while (length--) {
      convertedBuffer[length] = Math.min(1, buffer[length]) * 0x7fff;
    }
    return convertedBuffer.buffer;
  }

  private getAudioPayloadDurationMs(byteLength: number) {
    const bytesPerMs = (16000 * 2) / 1000;
    return byteLength / bytesPerMs;
  }

  public pauseTranscription() {
    const transitioned = this.transitionLifecycle("paused", "pause_called");
    if (!transitioned) {
      return;
    }

    this.recordState = "PAUSED";
    this.metricsCollector.markSessionPause();

    if (this.input) {
      this.input.disconnect();
    }
  }

  public resumeTranscription() {
    if (
      this.lifecyclePhase !== "paused" &&
      this.lifecyclePhase !== "running_recovering"
    ) {
      this.logLifecycle("phase_invalid", {
        attemptedTo: "running_live",
        reason: "resume_called",
      });
      return;
    }

    if (this.input && this.recordingNode) {
      this.input.connect(this.recordingNode);
    }
    this.recordState = "RECORDING";
    this.transitionLifecycle("running_live", "resume_called");
    this.metricsCollector.markSessionResume();
    this.emitResilienceStatus();
    if (
      this.audioContext &&
      typeof this.audioContext.resume === "function" &&
      this.audioContext.state !== "running"
    ) {
      void this.audioContext.resume();
    }

    if (!this.websocket) {
      return;
    }

    if (this.websocket.readyState === ReconnectingWebSocket.CLOSED) {
      this.awaitingReconnection = true;
      this.connectionState = "connecting";
      this.currentReconnectAttempt = null;
      this.nextReconnectDelayMs = 0;
      this.emitResilienceStatus();
      this.websocket.reconnect();
    }
  }

  public async stopTranscription(): Promise<Blob | null> {
    this.logStopFlow("stop_called");
    if (this.stopInFlightPromise) {
      this.logStopFlow("stop_reuse_inflight_promise");
      return this.stopInFlightPromise;
    }

    this.logStopFlow("stop_create_inflight_promise");
    this.stopInFlightPromise = this.performStopTranscription().finally(() => {
      this.logStopFlow("stop_inflight_promise_finally");
      this.stopInFlightPromise = null;
      this.stopFinalizationState = null;
    });

    return this.stopInFlightPromise;
  }

  private async freezeInputPhase() {
    this.transitionLifecycle("stopping_freeze_input", "stop_called");
    this.detachAudioPipeline();
    this.stopMediaStreamTracks();
    await this.closeAudioContext(true);
    await this.durableAudioSession?.flush("stop");
    this.intentionalStop = true;
    this.stopHealthChecks();
    this.removeBrowserNetworkListeners();
    this.lastReconnectAttempt = 0;
    this.connectionState = "stopped";
    this.currentReconnectAttempt = null;
    this.nextReconnectDelayMs = null;
    this.emitResilienceStatus();
  }

  private async finalizeServerPhase() {
    this.transitionLifecycle("stopping_finalize_server", "freeze_input_completed");
    await new Promise<void>((resolve) => {
      this.logStopFlow("perform_stop_wait_finish_start");
      const timeoutId = window.setTimeout(() => {
        this.logStopFlow("perform_stop_finish_timeout_fired");
        const stopState = this.stopFinalizationState;

        if (!stopState || stopState.settled) {
          this.logStopFlow("perform_stop_finish_timeout_noop", {
            reason: !stopState ? "no_stop_state" : "already_settled",
          });
          return;
        }

        if (!stopState.finishSent) {
          this.logStopFlow("perform_stop_finish_timeout_no_finish_sent");
          this.logLifecycle("phase_timeout", {
            phase: "stopping_finalize_server",
            timeoutMs: STOP_DRAIN_TIMEOUT_MS,
          });
          this.finishDeliveryState = this.websocket
            ? "timed_out"
            : "skipped_socket_closed";
          this.emitResilienceStatus();
        }

        this.emitPartialFallbackAsFinalIfPresent();
        this.settleStopFinalization();
      }, STOP_DRAIN_TIMEOUT_MS);

      this.stopFinalizationState = {
        resolve,
        timeoutId,
        finishSent: false,
        settled: false,
      };
      this.logStopFlow("perform_stop_wait_finish_state_initialized");

      if (this.websocket) {
        this.logStopFlow("perform_stop_wait_finish_with_websocket");
        this.trySendStopFinish();
        if (this.websocket.readyState === ReconnectingWebSocket.CLOSED) {
          this.logStopFlow("perform_stop_wait_finish_socket_already_closed");
          this.maybeHandleStopFinalizationOnClose();
        }
      } else {
        this.logStopFlow("perform_stop_wait_finish_no_websocket");
        this.finishDeliveryState = "skipped_socket_closed";
        this.emitResilienceStatus();
        this.emitPartialFallbackAsFinalIfPresent();
        this.settleStopFinalization();
      }
    });
  }

  private async cleanupPhase() {
    this.transitionLifecycle("stopping_cleanup", "finalize_server_completed");
    this.logStopFlow("perform_stop_force_close_websocket_before_cleanup");
    this.closeWebSocket();
    this.logStopFlow("perform_stop_force_close_websocket_done");
    await this.releaseRuntimeResources(true);
    this.logStopFlow("perform_stop_release_runtime_resources_done");
    await this.refreshConsultationStorageStatus(true);
    this.transitionBatchReprocessStatusAfterStop();
    this.durableAudioSession?.scheduleStorageDropAfterStop();
    this.logStopFlow("perform_stop_schedule_consultation_storage_ttl");
    this.logStopFlow("perform_stop_skip_post_stop_persistence_tasks");
    this.metricsCollector.markSessionStop();
    this.logStopFlow("perform_stop_metrics_mark_session_stop_done");
    this.metricsCollector.dispose();
    this.logStopFlow("perform_stop_metrics_dispose_done");
    this.emitResilienceStatus();
    this.logStopFlow("perform_stop_resilience_status_final_emitted");
    this.emit("stopped");
    this.logStopFlow("perform_stop_emit_stopped_done");
    this.transitionLifecycle("stopped", "stop_completed");
  }

  private async performStopTranscription(): Promise<Blob | null> {
    this.logStopFlow("perform_stop_begin");
    await this.freezeInputPhase();
    this.logStopFlow("perform_stop_freeze_input_done");
    await this.finalizeServerPhase();
    this.logStopFlow("perform_stop_wait_finish_done");
    await this.cleanupPhase();
    return null;
  }
}
